import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useAppAuth } from '../auth/appAuth';
import {
  configureAnalytics,
  setAnalyticsOrg,
  teardownAnalytics,
  trackPageview,
  trackClick,
  flush,
} from '../utils/analytics';

const DEV_BYPASS_AUTH = process.env.REACT_APP_BYPASS_AUTH === 'true';
const API_BASE = process.env.REACT_APP_API_SERVER_URL || 'https://127.0.0.1:5005';

/**
 * Mounts the analytics transport and records pageviews on route change.
 *
 * Renders nothing. Must live inside the Router (it uses useLocation) and is a
 * no-op until the user is authenticated, since ingest requires a bearer token.
 *
 * Page duration is measured as time between route changes, and reported on the
 * *outgoing* page -- so the duration for /admin/users arrives when the user
 * leaves it, not when they land on it.
 */
/**
 * The org an event belongs to, read from the URL.
 *
 * The app scopes org by `?org=<id>` (matching the backend's `org` query param)
 * or by an `/org/<id>` path segment. The path form deliberately covers BOTH the
 * customer dashboard (`/org/158/brand-tracker`) and the admin detail page
 * (`/admin/org/158`) -- the latter contains the former, so one pattern serves
 * both. Matching only the admin form left every dashboard event with a null
 * organization_id, which is precisely the traffic worth attributing.
 */
const orgFromLocation = (pathname: string, search: string): number | null => {
  const q = new URLSearchParams(search).get('org');
  if (q && /^\d+$/.test(q)) return Number(q);
  const m = pathname.match(/\/org\/(\d+)/);
  if (m) return Number(m[1]);
  return null;
};

const AnalyticsTracker: React.FC<{ organizationId?: number | null }> = ({ organizationId = null }) => {
  const location = useLocation();
  const { isAuthenticated, getAccessTokenSilently } = useAppAuth();

  const enteredAt = useRef<number>(Date.now());
  const lastPath = useRef<string | null>(null);
  const ready = useRef(false);

  // Held in a ref so the configure effect below does not re-run every time
  // Auth0 hands back a new function identity -- that churn used to tear the
  // transport down and back up on almost every render.
  const getToken = useRef(getAccessTokenSilently);
  useEffect(() => { getToken.current = getAccessTokenSilently; }, [getAccessTokenSilently]);

  // Wire the transport once the user is authenticated.
  useEffect(() => {
    if (!isAuthenticated && !DEV_BYPASS_AUTH) return;

    configureAnalytics(async (payload, useKeepalive) => {
      let headers: Record<string, string> = { 'Content-Type': 'application/json' };
      if (!(DEV_BYPASS_AUTH && !isAuthenticated)) {
        const token = await getToken.current();
        if (token) headers.Authorization = `Bearer ${token}`;
      }
      await fetch(`${API_BASE}/api/analytics/events`, {
        method: 'POST',
        headers,
        body: JSON.stringify(payload),
        // keepalive lets the request survive page teardown. Unlike sendBeacon,
        // it still allows the Authorization header our API requires.
        keepalive: useKeepalive,
      });
    }, organizationId ?? orgFromLocation(window.location.pathname, window.location.search));

    ready.current = true;
    return () => { teardownAnalytics(); ready.current = false; };
  }, [isAuthenticated, organizationId]);

  // Keep the org in sync as the user moves between org-scoped routes.
  useEffect(() => {
    setAnalyticsOrg(organizationId ?? orgFromLocation(location.pathname, location.search));
  }, [organizationId, location.pathname, location.search]);

  // Pageview on every route change, carrying the previous page's dwell time.
  useEffect(() => {
    if (!ready.current) return;
    const path = location.pathname;
    if (lastPath.current === path) return;

    if (lastPath.current !== null) {
      trackPageview(lastPath.current, undefined, Date.now() - enteredAt.current);
    }
    enteredAt.current = Date.now();
    lastPath.current = path;
    trackPageview(path);
  }, [location.pathname]);

  // Flush (and close out the current page's duration) when the tab is hidden
  // or torn down. visibilitychange is the reliable one on mobile; pagehide
  // covers desktop navigation away.
  useEffect(() => {
    const finalize = () => {
      if (!ready.current) return;
      if (lastPath.current) {
        trackPageview(lastPath.current, undefined, Date.now() - enteredAt.current);
        enteredAt.current = Date.now();
      }
      void flush(true);
    };
    const onVisibility = () => { if (document.visibilityState === 'hidden') finalize(); };

    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('pagehide', finalize);
    return () => {
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('pagehide', finalize);
    };
  }, []);

  // Global click capture. Only reads declarative labels we put in the markup
  // (data-track) or a short trimmed text label -- never input values.
  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      if (!ready.current) return;
      const el = (e.target as HTMLElement)?.closest?.('[data-track],button,a') as HTMLElement | null;
      if (!el) return;

      const explicit = el.getAttribute('data-track');
      let label = explicit || el.getAttribute('aria-label') || '';
      if (!label) {
        const tag = el.tagName.toLowerCase();
        if (tag === 'input' || tag === 'textarea' || tag === 'select') return; // never read entered data
        label = (el.textContent || '').trim().slice(0, 60);
      }
      if (!label) return;
      trackClick(label, { tag: el.tagName.toLowerCase() });
    };

    document.addEventListener('click', onClick, true);
    return () => document.removeEventListener('click', onClick, true);
  }, []);

  return null;
};

export default AnalyticsTracker;
